import { ErrorModal } from "@dexteel/mesf-core";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import type { GridApi } from "ag-grid-community";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { useNavigate, useParams } from "react-router-dom";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import { getReportPdf } from "@/shared/repositories/ReportPdfRepository";
import { gridToPdfTable } from "@/shared/utils/gridToPdfTable";
import {
	ALL_SECTIONS,
	CHEMISTRY_ELEMENTS,
	EMPTY,
	formatDecimals,
	formatText,
} from "../models/RollTurnHistoryDetail";
import {
	useRollDetail,
	useRollNames,
	useRollTurns,
} from "../hooks/useRollTurnHistoryDetail";
import { RollChemistryPanel } from "./RollChemistryPanel";
import { RollHeaderPanel } from "./RollHeaderPanel";
import { RollTurnHistoryGrid } from "./RollTurnHistoryGrid";

// The screen's fields in the order and blocks of the RollShop print.
const PDF_FIELD_BLOCKS: { column: number; labels: string[] }[] = [
	{ column: 0, labels: ["Mill Size", "Roll Status"] },
	{
		column: 0,
		labels: [
			"Roll Blank Diameter",
			"Roll New Diameter",
			"Roll Current Diameter",
			"Roll Scrap Diameter",
			"Value per 1000th Inch",
			"Percent Remaining",
			"Roll Application",
			"Roll Material",
			"Roll Hardness",
			"Roll Shell Depth",
			"Roll Barrel Description",
		],
	},
	{ column: 0, labels: ["Roll Cost", "Roll Current Value"] },
	{
		column: 1,
		labels: [
			"Purchase Order",
			"PO Item Number",
			"Supplier Name",
			"Supplier Invoice",
			"Cost Center",
		],
	},
	{ column: 1, labels: ["Date Roll Ordered", "Date Roll Received"] },
	{ column: 1, labels: ["Date Roll First Turned", "Date Roll Last Turned"] },
	{ column: 1, labels: ["Remove Reason", "Date Removed"] },
	{ column: 1, labels: ["Assigned Set", "Assigned Position"] },
];
import { RollTurnHistoryToolbar } from "./RollTurnHistoryToolbar";

// An empty state fills the space a report's grid would take, instead of leaving a short
// card floating over the background.
const FILLER_SX = {
	flex: 1,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
} as const;

const DEFAULT_LAYOUT = { left: 42, right: 58 };

export const RollTurnHistoryDetail = () => {
	const { rollName: rollNameParam } = useParams<{ rollName?: string }>();
	const navigate = useNavigate();
	const [error, setError] = useState("");

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "roll-turn-history-detail-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const rollName = rollNameParam ?? null;

	const namesQuery = useRollNames();
	const detailQuery = useRollDetail(rollName);
	const turnsQuery = useRollTurns(rollName);

	useEffect(() => {
		const failed = namesQuery.error ?? detailQuery.error ?? turnsQuery.error;
		if (failed) setError((failed as Error).message);
	}, [namesQuery.error, detailQuery.error, turnsQuery.error]);

	const handleRollNameChange = useCallback(
		(next: string | null) => {
			// Replace, so Back leaves the page instead of stepping through every roll looked at.
			navigate(
				next
					? `/reports/roll-turn-history-detail/${encodeURIComponent(next)}`
					: "/reports/roll-turn-history-detail",
				{ replace: true },
			);
		},
		[navigate],
	);

	const turns = turnsQuery.data ?? [];
	const isLoadingRoll = !!rollName && detailQuery.isInitialLoading;
	const roll = detailQuery.data ?? null;
	// A failed call also leaves the roll null. Saying "no roll matches" there would blame
	// the foundry number for what is a broken call, which is what a missing permission
	// looks like on the first deploy.
	const lookupFailed = !!detailQuery.error;
	const notFound = !!rollName && !isLoadingRoll && !roll && !lookupFailed;

	const turnsApiRef = useRef<GridApi | null>(null);
	const { exporting: exportingPdf, exportPdf } = useReportPdfExport(setError);
	const handleExportPdf = () => {
		const turnsApi = turnsApiRef.current;
		if (!roll || !turnsApi) return;
		const percent = roll.CurrentPercentageLeft;
		const values = new Map<string, string>(
			ALL_SECTIONS.flatMap((section) =>
				section.fields.map((field) => [field.label, field.value(roll)]),
			),
		);
		values.set(
			"Percent Remaining",
			percent == null ? EMPTY : `${percent.toFixed(2)} %`,
		);
		const groups = PDF_FIELD_BLOCKS.map((block) => ({
			column: block.column,
			fields: block.labels.map((label) => ({
				label,
				value: values.get(label) ?? EMPTY,
			})),
		}));
		const chemistry = {
			title: "Chemical Analysis",
			columns: CHEMISTRY_ELEMENTS.map((element) => ({
				header: element,
				width: 1,
				align: "center" as const,
				headerAlign: "center" as const,
			})),
			rows: [
				CHEMISTRY_ELEMENTS.map((element) => formatDecimals(roll[element], 3)),
			],
		};
		const turnHistory = gridToPdfTable(turnsApi, {
			title: `Turn History for Foundry Number: ${formatText(roll.Name)}`,
			emptyText: "This roll has no turns on record",
			aligns: { Comments: "left" },
			headerAligns: { Comments: "center" },
		});
		exportPdf(
			`Roll Turn History ${formatText(roll.Name)} ${moment().format("MM-DD-YYYY")}.pdf`,
			() =>
				getReportPdf({
					title: "Roll Turn History Detail",
					filters: [],
					heading: {
						label: "Roll Foundry Number",
						value: formatText(roll.Name),
					},
					groups,
					notes: [{ label: "Comments", value: formatText(roll.Comments) }],
					tables: [chemistry, turnHistory],
				}),
		);
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				// The app navbar is 64px tall and nothing above this Box reserves height, so
				// the page has to claim the rest itself. The 130px other reports subtract is
				// 66px too many, which is the white band left under them.
				height: "calc(100vh - 64px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Roll Turn History Detail
			</Typography>

			<Paper elevation={3}>
				<RollTurnHistoryToolbar
					rollName={rollName}
					options={namesQuery.data ?? []}
					loadingOptions={namesQuery.isInitialLoading}
					onRollNameChange={handleRollNameChange}
					onReset={() => handleRollNameChange(null)}
					canReset={rollName !== null}
					onExportPdf={handleExportPdf}
					canExportPdf={!!roll && !isLoadingRoll && !turnsQuery.isFetching}
					exportingPdf={exportingPdf}
				/>
			</Paper>

			{/* Master detail: the sheet on the left, the two tables on the right, so the
			    whole roll lands in one view instead of three blocks stacked down the page.
			    The split is draggable and the position is remembered, as on the
			    configuration screens. */}
			{!rollName && (
				<Paper elevation={3} sx={{ ...FILLER_SX }}>
					<Typography variant="body1" color="text.secondary">
						Pick a foundry number to see the roll
					</Typography>
				</Paper>
			)}

			{isLoadingRoll && (
				<Paper elevation={3} sx={{ ...FILLER_SX }}>
					<CircularProgress />
				</Paper>
			)}

			{notFound && (
				<Paper elevation={3} sx={{ ...FILLER_SX }}>
					<Typography variant="body1" color="text.secondary">
						No roll matches {rollName}
					</Typography>
				</Paper>
			)}

			{lookupFailed && !isLoadingRoll && (
				<Paper elevation={3} sx={{ ...FILLER_SX }}>
					<Typography variant="body1" color="text.secondary">
						The roll could not be loaded
					</Typography>
				</Paper>
			)}

			{!isLoadingRoll && roll && (
				<Group
					id="roll-turn-history-detail-group"
					orientation="horizontal"
					defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
					onLayoutChange={onLayoutChange}
					style={{ flex: 1, width: "100%", minHeight: 0 }}
				>
					<Panel id="left" minSize={25}>
						<Box sx={{ height: "100%", display: "flex" }}>
							<RollHeaderPanel roll={roll} />
						</Box>
					</Panel>

					<Separator
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "col-resize",
						}}
					>
						<Box
							sx={{
								width: 8,
								height: 48,
								borderRadius: 1,
								backgroundColor: "#e0e0e0",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								gap: "3px",
								transition: "background-color 0.2s",
								"&:hover": {
									backgroundColor: "primary.main",
									"& .grip-dot": { backgroundColor: "#fff" },
								},
							}}
						>
							{[...Array(5)].map((_, i) => (
								<Box
									key={`grip-dot-${i}`}
									className="grip-dot"
									sx={{
										width: 4,
										height: 4,
										borderRadius: "50%",
										backgroundColor: "#9e9e9e",
										transition: "background-color 0.2s",
									}}
								/>
							))}
						</Box>
					</Separator>

					<Panel id="right" minSize={30}>
						<Box
							sx={{
								height: "100%",
								display: "flex",
								flexDirection: "column",
								gap: 1,
							}}
						>
							<RollChemistryPanel roll={roll} />
							<RollTurnHistoryGrid
								rows={turns}
								loading={turnsQuery.isInitialLoading}
								onGridApi={(api) => {
									turnsApiRef.current = api;
								}}
							/>
						</Box>
					</Panel>
				</Group>
			)}
		</Box>
	);
};
